import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceLine
} from 'recharts';
import { useGetEconomicBalanceQuery } from 'store/services/admin/analyticsApi';
import { AnalyticsPeriod, EconomicBalanceDataPoint } from 'types/interfaces/admin/analytics';
import TimeRangeDropdown from './TimeRangeDropdown';

interface Props {
  period: AnalyticsPeriod;
  onPeriodChange: (period: AnalyticsPeriod) => void;
}

const CREATED_COLOR = '#22C55E';
const DESTROYED_COLOR = '#A855F7';

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || !payload.length) return null;

  const dataPoint: EconomicBalanceDataPoint = payload[0]?.payload;
  if (!dataPoint) return null;

  const date = new Date(dataPoint.date);
  const dateLabel = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  if (dataPoint.created === 0 && dataPoint.destroyed === 0) {
    return (
      <div style={{
        backgroundColor: '#1a1a1a',
        border: '1px solid #2a2a2a',
        borderRadius: '8px',
        padding: '10px 14px',
        color: '#fff',
        fontSize: '13px'
      }}>
        <div style={{ color: '#999', marginBottom: '4px' }}>{dateLabel}</div>
        <div style={{ color: '#666' }}>No activity</div>
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: '#1a1a1a',
      border: '1px solid #2a2a2a',
      borderRadius: '8px',
      padding: '10px 14px',
      color: '#fff',
      fontSize: '13px'
    }}>
      <div style={{ color: '#999', marginBottom: '6px' }}>{dateLabel}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: CREATED_COLOR, display: 'inline-block' }} />
        Created: {dataPoint.created.toLocaleString()} ({dataPoint.createdPct}%)
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: DESTROYED_COLOR, display: 'inline-block' }} />
        Destroyed: {dataPoint.destroyed.toLocaleString()} ({dataPoint.destroyedPct}%)
      </div>
    </div>
  );
};

const EconomicBalanceChart: React.FC<Props> = ({ period, onPeriodChange }) => {
  const { data, isLoading, error } = useGetEconomicBalanceQuery({ period });

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const chartData = data?.data || [];

  return (
    <div className="chart-card">
      <div className="chart-header">
        <div className="chart-header-left">
          <h2>Economic Balance</h2>
          <p className="chart-subtitle">Daily ratio of points created vs destroyed</p>
        </div>
        <div className="chart-header-right">
          <div className="chart-legend-multi">
            <div className="chart-legend">
              <span className="legend-dot created" />
              <span className="legend-label">Created</span>
            </div>
            <div className="chart-legend">
              <span className="legend-dot destroyed" />
              <span className="legend-label">Destroyed</span>
            </div>
          </div>
          <TimeRangeDropdown value={period} onChange={onPeriodChange} />
        </div>
      </div>

      <div className="chart-container">
        {isLoading ? (
          <div className="chart-loading">Loading...</div>
        ) : error ? (
          <div className="chart-error">Failed to load data</div>
        ) : data ? (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                stroke="#666"
                tick={{ fill: '#999', fontSize: 12 }}
                axisLine={{ stroke: '#2a2a2a' }}
                tickLine={false}
                interval={chartData.length > 60 ? Math.floor(chartData.length / 12) : 0}
              />
              <YAxis
                stroke="#666"
                tick={{ fill: '#999', fontSize: 12 }}
                axisLine={{ stroke: '#2a2a2a' }}
                tickLine={false}
                domain={[0, 100]}
                tickFormatter={(value) => `${value}%`}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.05)' }} />
              <ReferenceLine
                y={50}
                stroke="#666"
                strokeDasharray="4 4"
                label={{ value: '50%', position: 'right', fill: '#666', fontSize: 11 }}
              />
              <Bar
                dataKey="createdPct"
                stackId="balance"
                fill={CREATED_COLOR}
                radius={[0, 0, 0, 0]}
                name="Created"
              />
              <Bar
                dataKey="destroyedPct"
                stackId="balance"
                fill={DESTROYED_COLOR}
                radius={[4, 4, 0, 0]}
                name="Destroyed"
              />
            </BarChart>
          </ResponsiveContainer>
        ) : null}
      </div>

      {data && (
        <div className="chart-summary">
          Total Created: {data.totalCreated.toLocaleString()} | Total Destroyed: {data.totalDestroyed.toLocaleString()} | Net: {data.totalCreated >= data.totalDestroyed ? '+' : ''}{(data.totalCreated - data.totalDestroyed).toLocaleString()}
        </div>
      )}
    </div>
  );
};

export default EconomicBalanceChart;
